import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { ComponentPropsWithoutRef, ElementRef, ReactNode, forwardRef } from 'react';

import { cn } from 'styles';

const ScrollBar = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    {...props}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className,
    )}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));

ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

interface ScrollAreaProps {
  className?: string;
  children: ReactNode;
  orientation?: 'vertical' | 'horizontal';

  viewportClassName?: string;

  /**
   * Normal height/width can be defined
   */
  maxHeight?: number;

  /**
   * Normal height/width can be defined
   */
  maxWidth?: number;

  hideScrollbar?: boolean;
}

export function ScrollArea({
  className,
  children,
  maxHeight,
  maxWidth,
  viewportClassName,
  hideScrollbar,
  ...props
}: ScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root className={cn('relative overflow-hidden', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        // why? [&>div]:h-full It injects a div with display-table inbetween our viewport and children
        className={cn('h-full w-full rounded-[inherit] [&>div]:h-full', viewportClassName)}
        style={{ maxHeight, maxWidth }}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>

      {!hideScrollbar && <ScrollBar orientation={props.orientation} />}

      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
